/**
 * El levantamiento de **Yichang** (Pringles y Kellogg's), tal como lo guardan sus dos formularios.
 *
 * ⚠️ **No es el de P&G.** Son tablas propias (`yi_fichas_moderno`, `yi_fichas_bodega`,
 * `yi_fichas_mercado`, `yi_cierres_mercado`, migración 0242) y un formulario propio
 * (`entregables/yichang-2026/app-moderno` y `app-tradicional`, función `yichang-form`). Los dos
 * estudios comparten pantalla —«Levantamiento de información», con el cliente como primer
 * filtro— y nada más.
 *
 * ## Por qué `ficha` es `Record<string, unknown>` y no una interfaz cerrada
 *
 * A diferencia de P&G, el formulario de Yichang guarda la ficha ENTERA en el jsonb, sin lista
 * cerrada de campos (la lección de `bod-form`, que descartaba en silencio lo que no conocía). Eso
 * significa que mañana puede llegar una llave que hoy no existe, y la bandeja tiene que mostrarla
 * igual. Una interfaz cerrada prometería una forma que TypeScript no puede verificar en tiempo de
 * ejecución; por eso se lee con los accesores de abajo, que nunca asumen el tipo.
 */

export type InstrumentoYi = 'moderno' | 'bodegas' | 'mercados';

/**
 * El foco de marca de la bandeja. **Una visita cubre las dos marcas**: elegir una no descarta
 * fichas, enfoca la vista en la parte de cada ficha que es de esa marca.
 */
export type MarcaYi = 'las_dos' | 'pringles' | 'kelloggs';
export type MarcaCliente = Exclude<MarcaYi, 'las_dos'>;

export const NOMBRE_MARCA: Record<MarcaCliente, string> = {
  pringles: 'Pringles',
  kelloggs: "Kellogg's",
};

/**
 * Si la marca estaba en el punto de venta, **según la ficha**.
 *
 * 🔴 Son TRES estados y no dos. «No se puede saber» (la góndola no se pudo medir, el puesto
 * estaba cerrado, el bodeguero no contestó) no es «no está»: contarlo como ausencia fabrica un
 * cero que nadie midió. Es la regla del proyecto de que un «no está» nunca es un blanco.
 */
export type Presencia = 'presente' | 'ausente' | 'no_se_sabe';

export type Json = Record<string, unknown>;

interface Cabecera {
  id: string;
  supervisor: string | null;
  fecha: string;
  ocurridoEn: string | null;
  registradoEn: string | null;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  ficha: Json;
  /** Rutas de Storage ya aplanadas por el formulario: solo las que subieron bien. */
  fotos: string[];
  flags: string[];
  origin: string | null;
  duracionSeg: number | null;
}

export interface FichaYiModerno extends Cabecera {
  tiendaId: string | null;
  pdvId: string | null;
  /** Lo que el supervisor eligió de la lista o escribió. Es el nombre que se muestra. */
  tiendaTexto: string | null;
  cadena: string | null;
  formato: string | null;
}

export interface FichaYiBodega extends Cabecera {
  tiendaNombre: string | null;
  tiendaDireccion: string | null;
  tipoTienda: string | null;
  modoVisita: string | null;
  zona: string | null;
  distrito: string | null;
  esClienteYichang: string | null;
  codigoClienteYichang: string | null;
  /**
   * Si el bodeguero aceptó dar su contacto. **El contacto mismo NO se lee**: vive en
   * `yi_contactos`, que solo ve el admin, y no tiene nada que hacer en una bandeja de revisión.
   */
  contactoConsentido: boolean | null;
}

export interface FichaYiMercado extends Cabecera {
  mercadoNombre: string | null;
  mercadoDireccion: string | null;
  zona: string | null;
  distrito: string | null;
  puestoNumero: number | null;
  puestoNombre: string | null;
  /** `false` solo cuando el formulario dijo «No pude». Sin dato no se cuenta como levantado. */
  levantado: boolean;
  motivoNo: string | null;
}

export interface CierreYiMercado {
  id: string;
  supervisor: string | null;
  mercadoNombre: string | null;
  zona: string | null;
  distrito: string | null;
  fecha: string;
  ficha: Json;
  observacion: string | null;
  ocurridoEn: string | null;
  registradoEn: string | null;
  origin: string | null;
}

// ─────────────────────────── accesores que no asumen nada ───────────────────────────

/** Un objeto plano, o `null`. Un arreglo NO es un objeto acá. */
export const obj = (v: unknown): Json | null =>
  (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null);

/** Un texto con contenido, o `null`. El texto vacío es «sin respuesta», no una respuesta. */
export const str = (v: unknown): string | null =>
  (typeof v === 'string' && v.trim() !== '' ? v : null);

/** Un número finito, o `null`. Un `0` es un número: cero es una respuesta. */
export const num = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) ? v : null);

export const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** Los textos de una lista, sin los vacíos. */
export const strs = (v: unknown): string[] =>
  arr(v).filter((x): x is string => typeof x === 'string' && x.trim() !== '');

/** Un camino dentro de la ficha: `en(f, 'snacks', 'm5_estado_sku')`. */
export function en(raiz: unknown, ...camino: string[]): unknown {
  let v: unknown = raiz;
  for (const k of camino) {
    const o = obj(v);
    if (o === null) return undefined;
    v = o[k];
  }
  return v;
}
